import { useCallback, useMemo } from 'react';

import {
   CurrentHealthcareProviderP1IntegrationCertificatesMetadataQuery,
   CurrentHealthcareProviderP1IntegrationCertificatesSetCommand,
   HealthcareProviderP1IntegrationCertificateMetadata,
   HealthcareProviderP1IntegrationCertificatesMetadata,
   P1IntegrationCertificateProcessingFaultType,
   P1IntegrationCertificatesDepartmentCodeNotMatchFault,
   P1IntegrationCertificatesProcessingFault,
   P1IntegrationCertificatesSetCommandResult,
} from '@dreryk/egabinet-p1-frontend-api';

import { useCqrsCommand, useCqrsFind, UseCqrsQueryResult } from '@dreryk/command-query-react-common';
import { useFormController } from '@dreryk/react-common';
import {
   CertificateForCertificateUpload,
   dispatchSnackbarNegativeEvent,
   dispatchSnackbarPositiveEvent,
} from '@dreryk/react-components';

import { communicationWithP1TranslationKeys } from '../../../translations';

import { P1_CERTIFICATES_FORM_BEAN_INITIAL_VALUES } from './P1CertificatesForm.consts';
import type {
   CertificatesFormController,
   CertificateType,
   CertificateUploadNotificationLabels,
   P1CertificatesFormFields,
   SetP1CertificatesCommandResult,
   SetP1CertificatesVariables,
} from './P1CertificatesForm.types';
import { resolveCertificatesValidationWarnings } from './P1CertificatesForm.utils';

const useCertificatesFormController = (): CertificatesFormController => {
   return useFormController<P1CertificatesFormFields>(P1_CERTIFICATES_FORM_BEAN_INITIAL_VALUES, {
      resetAfterSubmit: true,
   });
};

const useCertificatesFormSubmitHandler = (
   onSubmit: SetP1CertificatesCommandResult['executeAsync']
): ((values: P1CertificatesFormFields) => Promise<void>) => {
   return useCallback(
      async (values) => {
         if (
            values.tlsCertificateFile &&
            values.tlsCertificatePassword &&
            values.wssCertificateFile &&
            values.wssCertificatePassword
         ) {
            await onSubmit({
               tlsCertificateContainer: values.tlsCertificateFile,
               tlsCertificatePassword: values.tlsCertificatePassword,
               wssCertificateContainer: values.wssCertificateFile,
               wssCertificatePassword: values.wssCertificatePassword,
            });
         }
      },
      [onSubmit]
   );
};

const useGetP1Certificates = (): UseCqrsQueryResult<HealthcareProviderP1IntegrationCertificatesMetadata | null> => {
   return useCqrsFind<HealthcareProviderP1IntegrationCertificatesMetadata | null>(
      () => new CurrentHealthcareProviderP1IntegrationCertificatesMetadataQuery()
   );
};

type UseSetP1CertificatesOptions = {
   onTlsCertificateInvalidPassword: VoidFunction;
   onTlsCertificateFault: (faultType: P1IntegrationCertificateProcessingFaultType) => void;
   onWssCertificateInvalidPassword: VoidFunction;
   onWssCertificateFault: (faultType: P1IntegrationCertificateProcessingFaultType) => void;
   onSuccess: VoidFunction;
};

const useSetP1Certificates = (options: UseSetP1CertificatesOptions): SetP1CertificatesCommandResult => {
   const { setData: setP1Certificates } = useGetP1Certificates();

   return useCqrsCommand<
      P1IntegrationCertificatesSetCommandResult,
      SetP1CertificatesVariables,
      P1IntegrationCertificatesProcessingFault | P1IntegrationCertificatesDepartmentCodeNotMatchFault
   >(
      (parameters) => {
         const command = new CurrentHealthcareProviderP1IntegrationCertificatesSetCommand();
         command.tlsCertificateContainer = parameters.tlsCertificateContainer;
         command.tlsCertificatePassword = parameters.tlsCertificatePassword;
         command.wssCertificateContainer = parameters.wssCertificateContainer;
         command.wssCertificatePassword = parameters.wssCertificatePassword;
         return command;
      },
      {
         onSuccess: (result) => {
            setP1Certificates(result.p1IntegrationCertificatesMetadataDTO);
            dispatchSnackbarPositiveEvent(communicationWithP1TranslationKeys.updateCertificatesSuccess);
            resolveCertificatesValidationWarnings(result.validationWarnings);
            options.onSuccess();
         },
         onFault: {
            'egabinet-p1-frontend-api:P1IntegrationCertificatesProcessingFault': ({
               tlsCertificateFault,
               wssCertificateFault,
            }) => {
               if (tlsCertificateFault) {
                  switch (tlsCertificateFault.type) {
                     case 'INVALID_CERTIFICATE_PASSWORD':
                        options.onTlsCertificateInvalidPassword();
                        break;
                     default:
                        options.onTlsCertificateFault(tlsCertificateFault.type);
                  }
               }

               if (wssCertificateFault) {
                  switch (wssCertificateFault.type) {
                     case 'INVALID_CERTIFICATE_PASSWORD':
                        options.onWssCertificateInvalidPassword();
                        break;
                     default:
                        options.onWssCertificateFault(wssCertificateFault.type);
                  }
               }
            },
            'egabinet-p1-frontend-api:P1IntegrationCertificatesDepartmentCodeNotMatchFault': () =>
               dispatchSnackbarNegativeEvent(
                  'healthCareInstitution:communicationWithP1:p1CertificatesForm:submissionErrors:departmentCodeNotMatch'
               ),
         },
      }
   );
};

const useCertificateNotificationLabels = (certificateType: CertificateType): CertificateUploadNotificationLabels => ({
   valid: `healthCareInstitution:communicationWithP1:p1CertificatesForm:${certificateType}:valid`,
   invalid: `healthCareInstitution:communicationWithP1:p1CertificatesForm:${certificateType}:invalid`,
});

const useMapCertificatesMetadataToCertificate = (
   certificateMetadata: HealthcareProviderP1IntegrationCertificateMetadata | undefined
): CertificateForCertificateUpload | undefined =>
   useMemo(() => {
      if (!certificateMetadata) {
         return undefined;
      }
      return {
         certificateName: certificateMetadata?.name || '',
         serialNumber: certificateMetadata?.serialNumber || '',
         validTo: new Date(certificateMetadata.validTo),
      };
   }, [certificateMetadata]);

export {
   useGetP1Certificates,
   useSetP1Certificates,
   SetP1CertificatesCommandResult,
   useCertificatesFormController,
   useCertificatesFormSubmitHandler,
   CertificatesFormController,
   useMapCertificatesMetadataToCertificate,
   useCertificateNotificationLabels,
   CertificateType,
};
